/**
 * Badge Component
 * Status indicators and labels
 */

export interface BadgeProps {
    children: React.ReactNode;
    variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
    size?: 'sm' | 'md' | 'lg';
    rounded?: boolean;
    className?: string;
}

export function Badge({
    children,
    variant = 'default',
    size = 'md',
    rounded = false,
    className = '',
}: BadgeProps) {
    const variantClasses = {
        default: 'bg-gray-100 text-gray-700',
        success: 'bg-green-100 text-green-700',
        warning: 'bg-amber-100 text-amber-700',
        error: 'bg-red-100 text-red-700',
        info: 'bg-blue-100 text-blue-700',
    };

    const sizeClasses = {
        sm: 'px-2 py-0.5 text-xs',
        md: 'px-2.5 py-1 text-sm',
        lg: 'px-3 py-1.5 text-base',
    };

    return (
        <span
            className={`
                inline-flex items-center font-medium
                ${variantClasses[variant]}
                ${sizeClasses[size]}
                ${rounded ? 'rounded-full' : 'rounded'}
                ${className}
            `}
        >
            {children}
        </span>
    );
}

export function StatusBadge({ status }: { status: string }) {
    const statusConfig: Record<string, BadgeProps['variant']> = {
        active: 'success',
        inactive: 'default',
        pending: 'warning',
        approved: 'success',
        rejected: 'error',
        draft: 'default',
        published: 'success',
        archived: 'default',
    };

    return (
        <Badge variant={statusConfig[status.toLowerCase()] || 'default'} rounded>
            {status}
        </Badge>
    );
}

export default Badge;
